import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import Input from '../../components/input/Input';
import DatePicker from '../../components/datepicker/DatePicker';
import DropDown from '../../components/dropdown/DropDown';
import { MonetizationFormData, FormValues } from '../../pages/MonetizationNew';
import { useState, useRef, useEffect } from 'react';
import { validateDrawerByCuit } from '../../utils/validationUtils';

interface PromissoryNoteSectionProps {
  formData: MonetizationFormData;
  formValues: FormValues;
  updateFormValues: (updates: Partial<FormValues>) => void;
}

const PromissoryNoteSection = ({ formData, formValues, updateFormValues }: PromissoryNoteSectionProps) => {
  const [drawerError, setDrawerError] = useState<string>('');
  const [lastValidatedCuit, setLastValidatedCuit] = useState<string>('');
  const [drawerManuallyEdited, setDrawerManuallyEdited] = useState(false);
  const [cuitLengthError, setCuitLengthError] = useState<string>('');
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup effect para limpiar timeout al desmontar
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Update due_date and estimated_payment_date when relevant fields change
  useEffect(() => {
    let updatedFields: Partial<FormValues> = {};
    
    // Calculate due_date when issue_date or term_in_days changes
    if (formValues.issue_date && formValues.term_in_days !== undefined && formValues.term_in_days !== null) {
      const issueDate = new Date(formValues.issue_date);
      const dueDate = new Date(issueDate);
      dueDate.setDate(dueDate.getDate() + formValues.term_in_days);
      
      const formattedDueDate = dueDate.toISOString().split('T')[0];
      if (formattedDueDate !== formValues.due_date) {
        updatedFields.due_date = formattedDueDate;
        
        // For promissory notes, estimated_payment_date = due_date (no clearing)
        if (formattedDueDate !== formValues.estimated_payment_date) {
          updatedFields.estimated_payment_date = formattedDueDate;
        }
      }
    }
    
    // Update all changed fields in a single call
    if (Object.keys(updatedFields).length > 0) {
      updateFormValues(updatedFields);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues.issue_date, formValues.term_in_days]);

  const creditorItems = formData.creditors?.map(creditor => ({
    label: creditor.name,
    disabled: false
  })) || [];

  const handleBankingEntityChange = (selectedLabel: string) => {
    const creditor = formData.creditors?.find(c => c.name === selectedLabel);
    if (creditor) {
      updateFormValues({ 
        banking_entity_id: creditor.id,
        banking_entity_name: creditor.name
      });
    }
  };

  const handleDrawerCuitChange = async (cuit: string) => {
    // Limpiar timeout previo para la validación de longitud
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Actualizar el valor inmediatamente
    updateFormValues({ 
      drawer: { 
        cuit: cuit,
        company_name: formValues.drawer?.company_name || ''
      } 
    });

    // Limpiar error previo inmediatamente
    setCuitLengthError('');

    // Crear timeout para validación de longitud con delay de 1 segundo
    debounceTimeoutRef.current = setTimeout(() => {
      // Validar longitud del CUIT después del delay
      if (cuit && cuit.length !== 11) {
        setCuitLengthError('El CUIT debe tener exactamente 11 dígitos');
      } else {
        setCuitLengthError('');
      }
    }, 1000);

    await validateDrawerByCuit({
      cuit,
      formValues,
      updateFormValues,
      setDrawerError,
      setValidatingDrawer: () => {},
      lastValidatedCuit,
      setLastValidatedCuit,
      drawerManuallyEdited,
      setDrawerManuallyEdited,
      debounceTimeoutRef
    });
  };

  const handleDrawerCompanyNameChange = (companyName: string) => {
    // Marcar que el drawer fue editado manualmente
    setDrawerManuallyEdited(true);
    
    updateFormValues({ 
      drawer: { 
        cuit: formValues.drawer?.cuit || '',
        company_name: companyName 
      } 
    });
    
    // Ejecutar validación para mostrar el mensaje de edición
    if (formValues.drawer?.cuit && formValues.drawer.cuit.length === 11) {
      validateDrawerByCuit({
        cuit: formValues.drawer.cuit,
        formValues: {
          ...formValues,
          drawer: {
            ...formValues.drawer,
            company_name: companyName
          }
        },
        updateFormValues,
        setDrawerError,
        setValidatingDrawer: () => {},
        lastValidatedCuit,
        setLastValidatedCuit,
        drawerManuallyEdited: true,
        setDrawerManuallyEdited,
        debounceTimeoutRef
      });
    }
  };

  const getSelectedBankingEntity = () => {
    // For edit mode, use the banking_entity_name from the endpoint if available
    if (formValues.banking_entity_name) {
      return formValues.banking_entity_name;
    }
    // Fallback to finding by ID in creditors
    const creditor = formData.creditors?.find(c => c.id === formValues.banking_entity_id);
    return creditor?.name || 'Seleccionar';
  };

  // Helper function for endorsed dropdown
  const getSelectedEndorsed = () => {
    if (formValues.endorsed === true) return 'Sí';
    if (formValues.endorsed === false) return 'No';
    return 'Seleccionar';
  };

  const handleEndorsedChange = (selectedLabel: string) => {
    const endorsed = selectedLabel === 'Sí' ? true : false;
    updateFormValues({ endorsed });
  };

  const endorsedItems = [
    { label: 'Sí', disabled: false },
    { label: 'No', disabled: false }
  ];

  return (
    <>
      {/* Datos del pagaré */}
      <Card margin={{ bottom: '4' }}>
        <Card margin={{ bottom: '3' }}>
          <H variant="secondary-2" type='4' weight='bold' modifier='strong'>
            Datos del pagaré
          </H>
        </Card>
        
        {/* Fila 1 */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px', alignItems: 'end' }}>
          <Input
            label="Monto bruto *"
            type="number"
            value={formValues.monetization_amount?.toString() || ''}
            onChange={(e) => updateFormValues({ monetization_amount: parseFloat(e.target.value) || null })}
            size="md"
            inputStatus="normal"
          />
          <Input
            label="Divisa"
            value={formData.currency}
            readOnly
            size="md"
            modifier="weak"
          />
          <Card>
            <P withMargin={false} variant='neutral' type='4' modifier='strong'>
              Fecha de emisión *
            </P>
            <Card margin={{ top: '2' }}>
              <DatePicker
                defaultValue={formValues.issue_date}
                onChange={(date: string) => updateFormValues({ issue_date: date })}
                disabled={false}
                displayFormat="DD/MM/AA"
              />
            </Card>
          </Card>
          <Card>
            <P withMargin={false} variant='neutral' type='4' modifier='weak'>
              Fecha de vencimiento *
            </P>
            <Card margin={{ top: '2' }}>
              <DatePicker
                defaultValue={formValues.due_date}
                onChange={() => {}}
                disabled={true}
                displayFormat="DD/MM/AA"
              />
            </Card>
          </Card>
        </div>

        {/* Fila 2 */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px', alignItems: 'end' }}>
          <DropDown
            id="banking_entity"
            label="Entidad bancaria *"
            title={getSelectedBankingEntity()}
            items={creditorItems}
            onChange={handleBankingEntityChange}
            size="md"
            marginTop="4px"
          />
          <Input
            label="Plazo en dias *"
            type="number"
            value={formValues.term_in_days?.toString() || ''}
            onChange={(e) => {
              const value = e.target.value;
              const numValue = value === '' ? 0 : parseInt(value);
              if (!isNaN(numValue)) {
                updateFormValues({ term_in_days: numValue });
              }
            }}
            size="md"
            inputStatus="normal"
          />
          <Card>
            <P withMargin={false} variant='neutral' type='4' modifier='strong'>
              Endosado *
            </P>
            <Card margin={{ top: '2' }}>
              <DropDown
                id="endorsed"
                label=""
                title={getSelectedEndorsed()}
                items={endorsedItems}
                onChange={handleEndorsedChange}
                size="md"
                marginTop="4px"
              />
            </Card>
          </Card>
          <Input
            label="ID E-Pagare *"
            value={formValues.e_promissory_note_id || ''}
            onChange={(e) => updateFormValues({ e_promissory_note_id: e.target.value })}
            size="md"
            maxLength={250}
            inputStatus="normal"
          />
        </div>

        {/* Fila 3 */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', alignItems: 'end' }}>
          <Card>
            <P withMargin={false} variant='neutral' type='4' modifier='weak'>
              Fecha estimada de pago *
            </P>
            <Card margin={{ top: '2' }}>
              <DatePicker
                defaultValue={formValues.estimated_payment_date || new Date().toISOString().split('T')[0]}
                onChange={(date: string) => updateFormValues({ estimated_payment_date: date })}
                disabled={true}
                displayFormat="DD/MM/AA"
              />
            </Card>
          </Card>
        </div>
      </Card>

      {/* Datos de librador */}
      <Card margin={{ bottom: '4' }}>
        <Card margin={{ bottom: '3' }}>
          <H variant="secondary-2" type='4' weight='bold' modifier='strong'>
            Datos de librador
          </H>
        </Card>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', alignItems: 'end' }}>
          <Input
            label="Librador *"
            value={formValues.drawer?.company_name || ''}
            onChange={(e) => handleDrawerCompanyNameChange(e.target.value)}
            size="md"
            maxLength={250}
            inputStatus="normal"
          />
          <Input
            label="Tipo de segmentación *"
            value=""
            readOnly
            size="md"
            modifier="weak"
          />
          <Input
            label="CUIT Librador *"
            type="number"
            value={formValues.drawer?.cuit || ''}
            onChange={(e) => handleDrawerCuitChange(e.target.value)}
            size="md"
            maxLength={250}
            onlyNumbers
            inputStatus={cuitLengthError ? "error" : "normal"}
          />
          <Input
            label="Nombre del librador *"
            value=''
            readOnly
            size="md"
            modifier="weak"
          />
        </div>
        
        {/* Mensaje de error del drawer */}
        {(drawerError || cuitLengthError) && (
          <div style={{ marginTop: '8px' }}>
            {cuitLengthError && (
              <P variant="danger" type="3">
                {cuitLengthError}
              </P>
            )}
            {drawerError && (
              <P variant="danger" type="3">
                {drawerError}
              </P>
            )}
          </div>
        )}
      </Card>
    </>
  );
};

export default PromissoryNoteSection;